import { formatCurrency } from '@crm/shared';
import { Badge, Button, cn, Spinner } from '@crm/ui';
import { Eye, MessageSquare, Settings2, Store } from 'lucide-react';
import { Link } from 'react-router-dom';
import { EmptyState, StatusHeader, TONE_BADGE } from '@/components';
import { BILLING_ROUTES } from '@/modules/billing/constants';
import { type Unit, useUnits } from '@/modules/company';
import { type PhoneLine, usePhoneLines } from '../api';
import { linhaDaUnidade, situacaoDaUnidade } from './unit-status';

function detalhe(unit: Unit, linha: PhoneLine | null): string {
  const numero = unit.whatsappFrom ?? linha?.phoneNumber;
  if (!numero) return 'Sem numero';

  return linha ? `${numero} - ${formatCurrency(linha.monthlyPrice)} por mes` : numero;
}

function UnidadeItem({ unit, linha }: { unit: Unit; linha: PhoneLine | null }) {
  const situacao = situacaoDaUnidade(unit, linha);
  const nova = situacao.setup === 'nova';

  return (
    <li className="py-4 first:pt-0 last:pb-0">
      <StatusHeader
        icon={<MessageSquare className="h-5 w-5" />}
        tone={situacao.tone}
        title={unit.name}
        detail={detalhe(unit, linha)}
        action={
          <div className="flex flex-wrap items-center gap-3">
            <Badge variant="outline" className={cn(TONE_BADGE[situacao.tone])}>
              {situacao.label}
            </Badge>

            <Button asChild variant={nova ? 'default' : 'outline'}>
              <Link to={nova ? BILLING_ROUTES.unitSetup(unit.id) : BILLING_ROUTES.unit(unit.id)}>
                {nova ? <Settings2 className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                {nova ? 'Configurar' : 'Ver'}
              </Link>
            </Button>
          </div>
        }
      />
    </li>
  );
}

export function WhatsappUnits() {
  const units = useUnits();
  const lines = usePhoneLines();

  if (units.isLoading || lines.isLoading) {
    return (
      <div className="flex justify-center py-10">
        <Spinner className="h-6 w-6 text-muted-foreground" />
      </div>
    );
  }

  const unidades = units.data?.data ?? [];
  const linhas = lines.data?.data ?? [];

  if (unidades.length === 0) {
    return <EmptyState icon={<Store />} title="Cadastre uma unidade antes" />;
  }

  return (
    <ul className="divide-y divide-border">
      {unidades.map((unit) => (
        <UnidadeItem key={unit.id} unit={unit} linha={linhaDaUnidade(linhas, unit.id)} />
      ))}
    </ul>
  );
}
